import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import {
  AuthStatePersistenceService,
  AuthToken,
  SyncedAuthState,
} from '@spartacus/core';

/**
 * Name of the lightweight flag cookie that signals "user is authenticated in
 * the legacy Spartacus app" to the new Next.js storefront. The cookie holds
 * no token material — only a boolean hint (`1`) — and lets the Next.js server
 * middleware avoid redirecting Spartacus-authenticated users to the login
 * page on first navigation. See USONESHOP-2501.
 */
const AUTH_HINT_COOKIE_NAME = 'vsca_auth_hint';

/**
 * Name of the punchout-session flag cookie (see punchout.service.ts, which
 * sets it at punchout login). Cleared here, in lockstep with
 * `vsca_auth_hint`, whenever the access token goes away — logout should end
 * a punchout session's visibility hint just as reliably as it ends the auth
 * hint, even though this service never sets it itself (only regular and
 * punchout logins both clear the token the same way, but only punchout
 * login should ever turn the flag on).
 */
const PUNCHOUT_HINT_COOKIE_NAME = 'vsca_punchout_hint';

const NUMERIC_STRING = /^-?\d+$/;

@Injectable()
export class MyAuthStatePersistenceService extends AuthStatePersistenceService {
  /**
   * In addition to the standard auth state synchronization with browser
   * storage, drive a non-sensitive flag cookie that the Next.js storefront
   * reads in its server middleware to bridge sessions across the two apps
   * (USONESHOP-2501). The cookie's lifetime tracks the access token's
   * `expires_at` so it never outlives the underlying session.
   */
  public initSync(): void {
    super.initSync();
    this.subscription.add(
      this.authStorageService
        .getToken()
        .subscribe((token) => this.syncAuthHintCookie(token)),
    );
  }

  /**
   * Gets and transforms state from different sources into the form that should
   * be saved in storage.
   */
  protected getAuthState(): Observable<SyncedAuthState> {
    return combineLatest([
      this.authStorageService.getToken().pipe(
        filter((state) => !!state),
        map((state) => {
          return {
            ...state,
          };
        }),
      ),
      this.userIdService.getUserId(),
      this.authRedirectStorageService.getRedirectUrl(),
    ]).pipe(
      map(([authToken, userId, redirectUrl]) => {
        let token = authToken;
        if (token) {
          token = { ...token };
        }

        return { token, userId, redirectUrl };
      }),
    );
  }

  /**
   * Sets `vsca_auth_hint=1` while a valid access token is present and clears
   * it otherwise. The cookie is deliberately non-`HttpOnly` (we set it from
   * the client) and host-only (no `Domain=`) — Spartacus and Next.js share
   * the same host per environment, so a host-scoped cookie is sufficient.
   */
  private syncAuthHintCookie(token: AuthToken | null | undefined): void {
    if (typeof document === 'undefined') return;

    const attrs = `Path=/; SameSite=Lax${
      typeof location !== 'undefined' && location.protocol === 'https:'
        ? '; Secure'
        : ''
    }`;

    if (token?.access_token) {
      const expires = MyAuthStatePersistenceService.parseExpiresAt(
        token.expires_at,
      );
      const expiresAttr = expires ? `; Expires=${expires.toUTCString()}` : '';
      document.cookie = `${AUTH_HINT_COOKIE_NAME}=1; ${attrs}${expiresAttr}`;
    } else {
      document.cookie = `${AUTH_HINT_COOKIE_NAME}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; ${attrs}`;
      document.cookie = `${PUNCHOUT_HINT_COOKIE_NAME}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; ${attrs}`;
    }
  }

  /**
   * Parses Spartacus' `AuthToken.expires_at` into a `Date`. The OAuth library
   * stores it as either a numeric millisecond timestamp string or an ISO date
   * string; we accept both. Returns `null` for missing or unparseable values
   * so callers can fall back to a session cookie.
   */
  private static parseExpiresAt(value: string | undefined): Date | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const ms = NUMERIC_STRING.test(trimmed)
      ? Number.parseInt(trimmed, 10)
      : Date.parse(trimmed);

    return Number.isFinite(ms) ? new Date(ms) : null;
  }
}
